// LoginModal.jsx (polished)
import React, { useState, useContext, useEffect, useRef } from "react";
import styles from "./login.module.css";
import loginImg from "./assets/img1.png";
import { AuthContext } from "../../context/AuthContext";

const OTP_TIMEOUT_SECONDS = 30; // change to 600 for 10 minutes in real usage

const LoginModal = ({ show, onClose }) => {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(OTP_TIMEOUT_SECONDS);
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [resendAllowed, setResendAllowed] = useState(false);

  const { sendOtp, verifyOtp, isLoggedIn, user } = useContext(AuthContext);

  const mobileRef = useRef(null);
  const otpRef = useRef(null);

  // Reset/initial focus when modal opens/closes
  useEffect(() => {
    if (show) {
      setTimeout(() => mobileRef.current?.focus(), 100);
    } else {
      setMobile("");
      setOtp("");
      setOtpSent(false);
      setTimer(OTP_TIMEOUT_SECONDS);
      setShowMessage(false);
      setMessage("");
      setLoadingSend(false);
      setLoadingVerify(false);
      setResendAllowed(false);
    }
  }, [show]);

  // Close modal automatically if context shows logged in
  useEffect(() => {
    if (isLoggedIn && show) {
      // small delay so user sees any success toast
      setTimeout(() => onClose?.(), 600);
    }
  }, [isLoggedIn, show, onClose]);

  // Timer for OTP countdown
  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (otpSent && timer === 0) {
      setResendAllowed(true);
      if (interval) clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  const mobilePattern = /^[0-9]{10}$/;

  const showTempMessage = (msg, ms = 2500) => {
    setMessage(msg);
    setShowMessage(true);
    clearTimeout(showTempMessage._t);
    showTempMessage._t = setTimeout(() => setShowMessage(false), ms);
  };

  const handleGetOtp = async () => {
    if (!mobilePattern.test(mobile)) {
      showTempMessage("Please enter a valid 10-digit mobile number");
      mobileRef.current?.focus();
      return;
    }
    if (loadingSend) return;

    try {
      setLoadingSend(true);
      const res = await sendOtp(mobile);
      if (res.ok) {
        setOtpSent(true);
        setTimer(OTP_TIMEOUT_SECONDS);
        setResendAllowed(false);
        showTempMessage("OTP sent successfully 📲");
        // focus OTP input shortly
        setTimeout(() => otpRef.current?.focus(), 300);
      } else {
        showTempMessage("Failed to send OTP: " + (res.message || "Try again"));
      }
    } catch (err) {
      showTempMessage("Network error while sending OTP");
      console.error("sendOtp error", err);
    } finally {
      setLoadingSend(false);
    }
  };

  const handleResend = async () => {
    if (!resendAllowed || loadingSend) return;
    setOtp("");
    await handleGetOtp();
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    console.log("handleLogin trigger");
    if (!mobilePattern.test(mobile)) {
      showTempMessage("Please enter a valid 10-digit mobile number");
      mobileRef.current?.focus();
      return;
    }
    console.log("Mobile validated");
    if (!otp || otp.trim().length === 0) {
      showTempMessage("Please enter OTP");
      otpRef.current?.focus();
      return;
    }
    console.log("OTP validated");
    if (loadingVerify) return;

console.log("Proceeding to verify OTP");
    try {
      setLoadingVerify(true);
      const res = await verifyOtp(mobile, otp);
      if (res.ok) {
        showTempMessage("Login Successful 🎉");
        // onClose will be triggered by isLoggedIn effect or you can call directly
        // small delay for UX
        setTimeout(() => onClose?.(), 700);
      } else {
        showTempMessage("OTP verification failed: " + (res.message || ""));
      }
    } catch (err) {
      console.error("verifyOtp error", err);
      showTempMessage("Network error while verifying OTP");
    } finally {
      setLoadingVerify(false);
    }
  };

  // handle Enter key for better UX
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (!otpSent) handleGetOtp();
      else handleLogin();
    }
  };

  if (!show) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <button className={styles.closeBtn} onClick={onClose}>
          &times;
        </button>

        <div className={styles.left}>
          <h2 className={styles.title}>LOGIN</h2>
          <p className={styles.desc}>
            Log-in to save your details, view donation history and{" "}
            <span className={styles.red}>download 80G certificate</span>.
          </p>
          <img src={loginImg} alt="Login Visual" className={styles.img1} />
        </div>

        <div className={styles.right}>
          <h2 className={styles.rightTitle}>LOGIN</h2>
          <div className={styles.underline}></div>
          <p className={styles.rightDesc}>
            We will send you an OTP on this mobile number and your registered mail id.
          </p>

          {!isLoggedIn ? (
            <>
              <input
                ref={mobileRef}
                type="text"
                className={styles.input}
                placeholder="Enter Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                disabled={otpSent}
                maxLength={10}
                autoComplete="tel"
              />

              {!otpSent ? (
                <button className={styles.otpBtn} onClick={handleGetOtp} disabled={loadingSend}>
                  {loadingSend ? "Sending..." : "GET OTP"}
                </button>
              ) : (
                <>
                  <input
                    ref={otpRef}
                    type="text"
                    className={styles.input}
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                  <p>Time remaining: {timer}s</p>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className={styles.otpBtn}
                      onClick={handleLogin}
                      disabled={loadingVerify || otp.length === 0 || timer === 0}
                    >
                      {loadingVerify ? "Verifying..." : "LOGIN"}
                    </button>

                    <button
                      className={styles.otpBtn}
                      onClick={handleResend}
                      disabled={!resendAllowed || loadingSend}
                      title={!resendAllowed ? "Please wait until timer ends to resend" : ""}
                    >
                      {loadingSend ? "Resending..." : "RESEND"}
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div>
              <p style={{ marginBottom: 8 }}>
                You are already logged in as <strong>{user?.name || user?.mobile}</strong>
              </p>
              <button
                className={styles.otpBtn}
                onClick={() => {
                  onClose();
                }}
              >
                CONTINUE
              </button>
            </div>
          )}

          {showMessage && <div className={styles.messagePopup}>{message}</div>}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
